import { useCallback, useEffect, useRef, useState } from 'react';
import { Mic } from '../primitives';
import { useVoiceToText } from '../../lib/voiceInput';
import { supabase } from '../../lib/supabase';
import {
  CORNERS, DRILL_TYPES, DIFFICULTIES, PHASES_OF_PLAY, CURRICULUM_PHASES,
  EQUIPMENT_OPTIONS, SUB_CORNERS,
  DEVELOPMENTAL_BANDS, MATURITY_CONTEXTS, CROSS_CUTTING_OVERLAYS,
  EVIDENCE_TYPES, EVIDENCE_STRENGTHS, TARGET_ROLES,
  completenessOf,
} from './drillFormConstants';
import type { DrillFormDraft, DrillTypeId, DifficultyId, PhaseOfPlayId } from './drillFormConstants';
import { cornerCssVar } from '../../lib/cornerColors';
import { glassModal, glassInput } from './liquidGlass';

interface DrillFormPanelProps {
  open: boolean;
  onClose: () => void;
  drillId: string | null | undefined;
  /** Called after a successful save so the parent can refresh any
   *  derived UI (e.g. canvas topbar title). */
  onSaved?: (draft: DrillFormDraft) => void;
}

/**
 * Slide-in drawer with every drill-metadata field web1 captured —
 * basics, phase + 4-corner mapping, setup, coaching, KB overlays.
 * Writes back to the `drills` table on Save.
 *
 * Triggered from the canvas topbar's "📝 Drill details" button.
 * Voice dictation is available on every free-text field via a Mic
 * chip — drives `useVoiceToText` and appends delta to the focused
 * field. Tactic: each text field that supports voice has its own
 * Mic + a focus-tracking ref so the same voice hook can target the
 * most-recent field cleanly.
 */
export function DrillFormPanel({ open, onClose, drillId, onSaved }: DrillFormPanelProps): React.JSX.Element | null {
  const [draft, setDraft] = useState<DrillFormDraft | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  // Load drill row when the panel opens.
  useEffect(() => {
    if (!open || !drillId) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from('drills')
        .select('id, name, description, duration_mins, drill_type, difficulty, primary_phase, sub_themes, curriculum_phase, corner_ids, primary_corner_id, sub_corner_ids, min_players, max_players, space_required, equipment, phase_objective, four_cm_connection, coaching_points, progressions, regressions, developmental_band, maturity_context, cross_cutting_overlays, evidence_type, evidence_strength, target_roles')
        .eq('id', drillId)
        .maybeSingle();
      if (cancelled) return;
      setLoading(false);
      if (error || !data) {
        alert(`Couldn't load drill metadata: ${error?.message ?? 'not found'}`);
        return;
      }
      const r = data as Record<string, unknown>;
      setDraft({
        name: (r.name as string) ?? '',
        description: (r.description as string) ?? '',
        duration_mins: (r.duration_mins as number) ?? 15,
        drill_type: (r.drill_type as DrillTypeId | null) ?? null,
        difficulty: (r.difficulty as DifficultyId) ?? 'INTERMEDIATE',
        primary_phase: (r.primary_phase as PhaseOfPlayId | null) ?? null,
        sub_themes: (r.sub_themes as string[] | null) ?? [],
        curriculum_phase: (r.curriculum_phase as string[] | null) ?? [],
        corner_ids: (r.corner_ids as string[] | null) ?? [],
        primary_corner_id: (r.primary_corner_id as string | null) ?? null,
        sub_corner_ids: (r.sub_corner_ids as string[] | null) ?? [],
        min_players: (r.min_players as number | null) ?? null,
        max_players: (r.max_players as number | null) ?? null,
        space_required: (r.space_required as string) ?? '',
        equipment: (r.equipment as string[] | null) ?? [],
        phase_objective: (r.phase_objective as string) ?? '',
        four_cm_connection: (r.four_cm_connection as string) ?? '',
        coaching_points: ensureMin((r.coaching_points as string[] | null) ?? [], 3),
        progressions: ensureMin((r.progressions as string[] | null) ?? [], 2),
        regressions: ensureMin((r.regressions as string[] | null) ?? [], 2),
        developmental_band: (r.developmental_band as string[] | null) ?? [],
        maturity_context: (r.maturity_context as string[] | null) ?? [],
        cross_cutting_overlays: (r.cross_cutting_overlays as string[] | null) ?? [],
        evidence_type: (r.evidence_type as string | null) ?? null,
        evidence_strength: (r.evidence_strength as string | null) ?? null,
        target_roles: (r.target_roles as string[] | null) ?? [],
      });
    })();
    return () => { cancelled = true; };
  }, [open, drillId]);

  // Voice: append-to-focused-field. Track which field is "focused" via
  // a ref + a setVoiceTarget setter that fields call onFocus.
  const voiceTargetRef = useRef<{ kind: 'name' | 'description' | 'space' | 'objective' | 'fourcm' | 'coaching' | 'progression' | 'regression' | 'sub_theme'; index?: number } | null>(null);
  const lastVoiceLenRef = useRef(0);
  const voice = useVoiceToText({
    onTranscript: (text) => {
      const delta = text.slice(lastVoiceLenRef.current);
      lastVoiceLenRef.current = text.length;
      if (!delta.trim() || !draft) return;
      const target = voiceTargetRef.current;
      if (!target) return;
      const append = (current: string) => (current ? `${current} ${delta.trim()}` : delta.trim());
      setDraft((prev) => {
        if (!prev) return prev;
        switch (target.kind) {
          case 'name': return { ...prev, name: append(prev.name) };
          case 'description': return { ...prev, description: append(prev.description) };
          case 'space': return { ...prev, space_required: append(prev.space_required) };
          case 'objective': return { ...prev, phase_objective: append(prev.phase_objective) };
          case 'fourcm': return { ...prev, four_cm_connection: append(prev.four_cm_connection) };
          case 'coaching': {
            const idx = target.index ?? 0;
            const next = [...prev.coaching_points];
            next[idx] = append(next[idx] ?? '');
            return { ...prev, coaching_points: next };
          }
          case 'progression': {
            const idx = target.index ?? 0;
            const next = [...prev.progressions];
            next[idx] = append(next[idx] ?? '');
            return { ...prev, progressions: next };
          }
          case 'regression': {
            const idx = target.index ?? 0;
            const next = [...prev.regressions];
            next[idx] = append(next[idx] ?? '');
            return { ...prev, regressions: next };
          }
          default: return prev;
        }
      });
    },
  });
  useEffect(() => {
    if (!voice.listening) lastVoiceLenRef.current = 0;
  }, [voice.listening]);

  const setVoiceTarget = useCallback((target: typeof voiceTargetRef.current) => {
    voiceTargetRef.current = target;
  }, []);

  // Save handler.
  const handleSave = useCallback(async () => {
    if (!draft || !drillId || saving) return;
    setSaving(true);
    setSavedMsg(null);
    const patch: Record<string, unknown> = {
      name: draft.name.trim() || 'Untitled drill',
      description: draft.description.trim(),
      duration_mins: draft.duration_mins || 15,
      drill_type: draft.drill_type,
      difficulty: draft.difficulty,
      primary_phase: draft.primary_phase,
      sub_themes: draft.sub_themes.map((s) => s.trim()).filter(Boolean),
      curriculum_phase: draft.curriculum_phase,
      corner_ids: draft.corner_ids,
      primary_corner_id: draft.primary_corner_id,
      sub_corner_ids: draft.sub_corner_ids,
      min_players: draft.min_players,
      max_players: draft.max_players,
      space_required: draft.space_required.trim(),
      equipment: draft.equipment,
      phase_objective: draft.phase_objective.trim(),
      four_cm_connection: draft.four_cm_connection.trim(),
      coaching_points: draft.coaching_points.map((s) => s.trim()).filter(Boolean),
      progressions: draft.progressions.map((s) => s.trim()).filter(Boolean),
      regressions: draft.regressions.map((s) => s.trim()).filter(Boolean),
      developmental_band: draft.developmental_band.length ? draft.developmental_band : null,
      maturity_context: draft.maturity_context.length ? draft.maturity_context : null,
      cross_cutting_overlays: draft.cross_cutting_overlays.length ? draft.cross_cutting_overlays : null,
      evidence_type: draft.evidence_type,
      evidence_strength: draft.evidence_strength,
      target_roles: draft.target_roles.length ? draft.target_roles : null,
    };
    const { error } = await supabase.from('drills').update(patch).eq('id', drillId);
    setSaving(false);
    if (error) {
      setSavedMsg(`Save failed: ${error.message}`);
      return;
    }
    setSavedMsg('✓ Drill details saved.');
    onSaved?.(draft);
    window.setTimeout(() => setSavedMsg(null), 1800);
  }, [draft, drillId, saving, onSaved]);

  if (!open) return null;

  return (
    <aside role="dialog" aria-label="Drill details" style={drawerStyle}>
      <header style={headerStyle}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Drill details</div>
          <h2 style={{ margin: 0, fontSize: 18 }}>{draft?.name?.trim() || 'New drill'}</h2>
        </div>
        {draft && <CompletenessBadge draft={draft} />}
        <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close drill details">✕</button>
      </header>

      <div style={{ overflowY: 'auto', flex: 1, padding: 14 }}>
        {loading && <p style={statusStyle}>Loading…</p>}
        {!loading && !draft && <p style={statusStyle}>No drill loaded.</p>}
        {draft && (
          <>
            {/* BASICS */}
            <Section label="Basics">
              <TextField
                label="Name"
                value={draft.name}
                onChange={(v) => setDraft({ ...draft, name: v })}
                onFocus={() => setVoiceTarget({ kind: 'name' })}
                voice={voice}
              />
              <TextAreaField
                label="Description"
                value={draft.description}
                onChange={(v) => setDraft({ ...draft, description: v })}
                onFocus={() => setVoiceTarget({ kind: 'description' })}
                voice={voice}
                rows={3}
              />
              <div style={twoColRow}>
                <NumberField
                  label="Duration (min)"
                  value={draft.duration_mins}
                  onChange={(v) => setDraft({ ...draft, duration_mins: v ?? 15 })}
                />
                <SelectField
                  label="Drill type"
                  value={draft.drill_type ?? ''}
                  onChange={(v) => setDraft({ ...draft, drill_type: (v || null) as DrillTypeId | null })}
                  options={[{ id: '', label: '—' }, ...DRILL_TYPES.map((d) => ({ id: d.id, label: d.label }))]}
                />
              </div>
              <ChipGroup
                label="Difficulty"
                options={DIFFICULTIES.map((d) => ({ id: d.id, label: d.label }))}
                value={[draft.difficulty]}
                onToggle={(id) => setDraft({ ...draft, difficulty: id as DifficultyId })}
                multi={false}
              />
            </Section>

            {/* PHASE + CORNERS */}
            <Section label="Curriculum mapping">
              <ChipGroup
                label="Phase of play (primary)"
                options={PHASES_OF_PLAY.map((p) => ({ id: p.id, label: `${p.glyph} ${p.label}` }))}
                value={draft.primary_phase ? [draft.primary_phase] : []}
                onToggle={(id) => setDraft({ ...draft, primary_phase: draft.primary_phase === id ? null : (id as PhaseOfPlayId) })}
                multi={false}
              />
              <ChipGroup
                label="Curriculum phase"
                options={CURRICULUM_PHASES.map((p) => ({ id: p, label: p }))}
                value={draft.curriculum_phase}
                onToggle={(id) => setDraft({ ...draft, curriculum_phase: toggleArr(draft.curriculum_phase, id) })}
                multi
              />
              <SubThemes draft={draft} setDraft={setDraft} setVoiceTarget={setVoiceTarget} voice={voice} />
              <ChipGroup
                label="4-Corner tags"
                options={CORNERS.map((c) => ({ id: c.id, label: `${c.icon} ${c.label}`, color: cornerCssVar(c.id) }))}
                value={draft.corner_ids}
                onToggle={(id) => {
                  const next = toggleArr(draft.corner_ids, id);
                  // If removing the current primary, clear it.
                  const primaryStillSet = draft.primary_corner_id && next.includes(draft.primary_corner_id);
                  setDraft({
                    ...draft,
                    corner_ids: next,
                    primary_corner_id: primaryStillSet ? draft.primary_corner_id : null,
                    sub_corner_ids: draft.sub_corner_ids.filter((sc) => {
                      const subCornerOwner = SUB_CORNERS.find((s) => s.label === sc)?.cornerId;
                      return subCornerOwner ? next.includes(subCornerOwner) : false;
                    }),
                  });
                }}
                multi
              />
              {draft.corner_ids.length > 0 && (
                <ChipGroup
                  label="Primary corner ★"
                  options={CORNERS.filter((c) => draft.corner_ids.includes(c.id)).map((c) => ({ id: c.id, label: `${c.icon} ${c.label}`, color: cornerCssVar(c.id) }))}
                  value={draft.primary_corner_id ? [draft.primary_corner_id] : []}
                  onToggle={(id) => setDraft({ ...draft, primary_corner_id: draft.primary_corner_id === id ? null : id })}
                  multi={false}
                />
              )}
              {draft.corner_ids.length > 0 && (
                <ChipGroup
                  label="Sub-corners"
                  options={SUB_CORNERS.filter((sc) => draft.corner_ids.includes(sc.cornerId)).map((sc) => ({ id: sc.label, label: sc.label }))}
                  value={draft.sub_corner_ids}
                  onToggle={(id) => setDraft({ ...draft, sub_corner_ids: toggleArr(draft.sub_corner_ids, id) })}
                  multi
                />
              )}
            </Section>

            {/* SETUP */}
            <Section label="Setup">
              <div style={twoColRow}>
                <NumberField
                  label="Min players"
                  value={draft.min_players}
                  onChange={(v) => setDraft({ ...draft, min_players: v })}
                />
                <NumberField
                  label="Max players"
                  value={draft.max_players}
                  onChange={(v) => setDraft({ ...draft, max_players: v })}
                />
              </div>
              <TextField
                label="Space required"
                placeholder="e.g. 30×40 yard grid"
                value={draft.space_required}
                onChange={(v) => setDraft({ ...draft, space_required: v })}
                onFocus={() => setVoiceTarget({ kind: 'space' })}
                voice={voice}
              />
              <ChipGroup
                label="Equipment"
                options={EQUIPMENT_OPTIONS.map((e) => ({ id: e, label: e }))}
                value={draft.equipment}
                onToggle={(id) => setDraft({ ...draft, equipment: toggleArr(draft.equipment, id) })}
                multi
              />
            </Section>

            {/* COACHING */}
            <Section label="Coaching context">
              <TextAreaField
                label="Phase objective"
                value={draft.phase_objective}
                onChange={(v) => setDraft({ ...draft, phase_objective: v })}
                onFocus={() => setVoiceTarget({ kind: 'objective' })}
                voice={voice}
                rows={2}
              />
              <TextAreaField
                label="4CM connection"
                value={draft.four_cm_connection}
                onChange={(v) => setDraft({ ...draft, four_cm_connection: v })}
                onFocus={() => setVoiceTarget({ kind: 'fourcm' })}
                voice={voice}
                rows={2}
              />
              <ListField
                label="Coaching points"
                values={draft.coaching_points}
                onChange={(arr) => setDraft({ ...draft, coaching_points: arr })}
                onFocusIdx={(i) => setVoiceTarget({ kind: 'coaching', index: i })}
                voice={voice}
                placeholder="What to observe + coach"
                min={3}
              />
              <ListField
                label="Progressions (harder)"
                values={draft.progressions}
                onChange={(arr) => setDraft({ ...draft, progressions: arr })}
                onFocusIdx={(i) => setVoiceTarget({ kind: 'progression', index: i })}
                voice={voice}
                placeholder="Make it harder…"
                min={2}
              />
              <ListField
                label="Regressions (easier)"
                values={draft.regressions}
                onChange={(arr) => setDraft({ ...draft, regressions: arr })}
                onFocusIdx={(i) => setVoiceTarget({ kind: 'regression', index: i })}
                voice={voice}
                placeholder="Make it easier…"
                min={2}
              />
            </Section>

            {/* KB OVERLAYS — collapsed by default */}
            <CollapsibleSection label="Knowledge-base overlays">
              <ChipGroup
                label="Developmental band"
                options={DEVELOPMENTAL_BANDS.map((b) => ({ id: b.id, label: b.label }))}
                value={draft.developmental_band}
                onToggle={(id) => setDraft({ ...draft, developmental_band: toggleArr(draft.developmental_band, id) })}
                multi
              />
              <ChipGroup
                label="Maturity context"
                options={MATURITY_CONTEXTS.map((m) => ({ id: m.id, label: m.label }))}
                value={draft.maturity_context}
                onToggle={(id) => setDraft({ ...draft, maturity_context: toggleArr(draft.maturity_context, id) })}
                multi
              />
              <ChipGroup
                label="Cross-cutting overlays"
                options={CROSS_CUTTING_OVERLAYS.map((c) => ({ id: c.id, label: c.label }))}
                value={draft.cross_cutting_overlays}
                onToggle={(id) => setDraft({ ...draft, cross_cutting_overlays: toggleArr(draft.cross_cutting_overlays, id) })}
                multi
              />
              <ChipGroup
                label="Evidence type"
                options={EVIDENCE_TYPES.map((e) => ({ id: e.id, label: e.label }))}
                value={draft.evidence_type ? [draft.evidence_type] : []}
                onToggle={(id) => setDraft({ ...draft, evidence_type: draft.evidence_type === id ? null : id })}
                multi={false}
              />
              <ChipGroup
                label="Evidence strength"
                options={EVIDENCE_STRENGTHS.map((e) => ({ id: e.id, label: e.label }))}
                value={draft.evidence_strength ? [draft.evidence_strength] : []}
                onToggle={(id) => setDraft({ ...draft, evidence_strength: draft.evidence_strength === id ? null : id })}
                multi={false}
              />
              <ChipGroup
                label="Target roles"
                options={TARGET_ROLES.map((t) => ({ id: t.id, label: t.label }))}
                value={draft.target_roles}
                onToggle={(id) => setDraft({ ...draft, target_roles: toggleArr(draft.target_roles, id) })}
                multi
              />
            </CollapsibleSection>
          </>
        )}
      </div>

      <footer style={footerStyle}>
        {savedMsg && <span style={{ color: savedMsg.startsWith('Save failed') ? '#c0392b' : '#27ae60', fontSize: 12 }}>{savedMsg}</span>}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!draft || saving}
          style={saveBtnStyle}
        >
          {saving ? 'Saving…' : '💾 Save details'}
        </button>
      </footer>
    </aside>
  );
}

// ---- Sub-components --------------------------------------------------------

function Section({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <section style={{ marginBottom: 18 }}>
      <h3 style={sectionTitleStyle}>{label}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </section>
  );
}

function CollapsibleSection({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <section style={{ marginBottom: 18 }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{ ...sectionTitleStyle, display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
      >
        <span>{open ? '▾' : '▸'}</span>
        <span>{label}</span>
      </button>
      {open && <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>{children}</div>}
    </section>
  );
}

function CompletenessBadge({ draft }: { draft: DrillFormDraft }): React.JSX.Element {
  const { score, max } = completenessOf(draft);
  const pct = (score / max) * 100;
  return (
    <div title={`${score}/${max} fields complete`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 60, height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? 'var(--state-good)' : 'var(--state-warn)', transition: 'width 200ms' }} />
      </div>
      <span style={{ fontSize: 11, opacity: 0.85 }}>{score}/{max}</span>
    </div>
  );
}

interface VoiceState {
  listening: boolean;
  start: () => void | Promise<void>;
  stop: () => void;
  status?: string | null;
}

function TextField({ label, value, onChange, onFocus, voice, placeholder }: { label: string; value: string; onChange: (v: string) => void; onFocus?: () => void; voice?: VoiceState; placeholder?: string }): React.JSX.Element {
  return (
    <label style={fieldStyle}>
      <span style={fieldLabelStyle}>
        {label}
        {voice && <Mic listening={voice.listening} mode="toggle" onStart={() => void voice.start()} onStop={voice.stop} />}
      </span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        style={inputStyle}
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, onFocus, voice, rows = 3 }: { label: string; value: string; onChange: (v: string) => void; onFocus?: () => void; voice?: VoiceState; rows?: number }): React.JSX.Element {
  return (
    <label style={fieldStyle}>
      <span style={fieldLabelStyle}>
        {label}
        {voice && <Mic listening={voice.listening} mode="toggle" onStart={() => void voice.start()} onStop={voice.stop} />}
      </span>
      <textarea
        value={value}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.4 }}
      />
    </label>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number | null; onChange: (v: number | null) => void }): React.JSX.Element {
  return (
    <label style={fieldStyle}>
      <span style={fieldLabelStyle}>{label}</span>
      <input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        style={inputStyle}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: readonly { id: string; label: string }[] }): React.JSX.Element {
  return (
    <label style={fieldStyle}>
      <span style={fieldLabelStyle}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, padding: '6px 10px' }}>
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </label>
  );
}

interface ChipGroupProps {
  label: string;
  options: readonly { id: string; label: string; color?: string }[];
  value: string[];
  onToggle: (id: string) => void;
  multi: boolean;
}
function ChipGroup({ label, options, value, onToggle, multi }: ChipGroupProps): React.JSX.Element {
  return (
    <div style={fieldStyle}>
      <span style={fieldLabelStyle}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((o) => {
          const active = value.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onToggle(o.id)}
              style={{
                padding: '5px 10px',
                fontSize: 12,
                background: active ? (o.color ?? 'rgba(39,174,96,0.25)') : 'rgba(255,255,255,0.05)',
                color: active ? '#fff' : '#fff',
                border: `1px solid ${active ? (o.color ?? '#27ae60') : 'rgba(255,255,255,0.15)'}`,
                borderRadius: 999,
                cursor: 'pointer',
                fontWeight: active ? 600 : 400,
              }}
              aria-pressed={active}
              title={multi ? 'Toggle' : 'Pick one'}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface ListFieldProps {
  label: string;
  values: string[];
  onChange: (arr: string[]) => void;
  onFocusIdx: (i: number) => void;
  voice?: VoiceState;
  placeholder?: string;
  min?: number;
}
function ListField({ label, values, onChange, onFocusIdx, voice, placeholder, min = 0 }: ListFieldProps): React.JSX.Element {
  return (
    <div style={fieldStyle}>
      <span style={fieldLabelStyle}>
        {label}{min > 0 && <span style={{ opacity: 0.5 }}> · {min}+ recommended</span>}
        {voice && <Mic listening={voice.listening} mode="toggle" onStart={() => void voice.start()} onStop={voice.stop} />}
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {values.map((v, i) => (
          <div key={i} style={{ display: 'flex', gap: 4 }}>
            <input
              type="text"
              value={v}
              placeholder={placeholder}
              onChange={(e) => onChange(values.map((x, j) => (j === i ? e.target.value : x)))}
              onFocus={() => onFocusIdx(i)}
              style={{ ...inputStyle, flex: 1 }}
            />
            <button
              type="button"
              onClick={() => onChange(values.filter((_, j) => j !== i))}
              style={removeBtnStyle}
              title="Remove"
              aria-label="Remove this item"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...values, ''])}
          style={addBtnStyle}
        >
          + Add point
        </button>
      </div>
    </div>
  );
}

function SubThemes({ draft, setDraft, setVoiceTarget, voice }: { draft: DrillFormDraft; setDraft: (d: DrillFormDraft) => void; setVoiceTarget: (t: { kind: 'sub_theme'; index?: number }) => void; voice: VoiceState }): React.JSX.Element {
  return (
    <ListField
      label="Sub-themes"
      values={draft.sub_themes.length > 0 ? draft.sub_themes : ['']}
      onChange={(arr) => setDraft({ ...draft, sub_themes: arr })}
      onFocusIdx={(i) => setVoiceTarget({ kind: 'sub_theme', index: i })}
      voice={voice}
      placeholder="e.g. progressing through midfield"
    />
  );
}

// ---- helpers --------------------------------------------------------------

function ensureMin(arr: string[], n: number): string[] {
  if (arr.length >= n) return arr;
  return [...arr, ...Array(n - arr.length).fill('')];
}

function toggleArr<T>(arr: T[], item: T): T[] {
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
}

// ---- styles ---------------------------------------------------------------

const drawerStyle: React.CSSProperties = {
  ...glassModal,
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: 'min(440px, 100vw)',
  borderRadius: 0,
  borderTop: 0,
  borderRight: 0,
  borderBottom: 0,
  display: 'flex',
  flexDirection: 'column',
  zIndex: 999,
  fontFamily: 'system-ui, sans-serif',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
};

const closeBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 6,
  width: 32,
  height: 32,
  cursor: 'pointer',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.7)',
  marginBottom: 8,
  paddingBottom: 4,
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 11,
  opacity: 0.7,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const inputStyle: React.CSSProperties = glassInput;

const removeBtnStyle: React.CSSProperties = {
  width: 28,
  background: 'transparent',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 4,
  cursor: 'pointer',
};

const addBtnStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  background: 'transparent',
  color: '#fff',
  border: '1px dashed rgba(255,255,255,0.25)',
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 11,
  cursor: 'pointer',
  marginTop: 2,
};

const twoColRow: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 10,
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 14px',
  borderTop: '1px solid rgba(255,255,255,0.1)',
};

const saveBtnStyle: React.CSSProperties = {
  padding: '8px 16px',
  background: '#27ae60',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const statusStyle: React.CSSProperties = {
  textAlign: 'center',
  opacity: 0.7,
  padding: 16,
};
